import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isRunningInNativeApp, native } from '@muzly/shared';

import {
  clearProfileImage,
  loadProfileImage,
  saveProfileImage,
} from './profileImage';

import type { PickImageResult } from '@muzly/shared/types';

/**
 * useProfileImage — 프로필 사진 하나를 읽고, 바꾸고, 지운다.
 *
 * 사진을 고르는 일은 네이티브만 할 수 있다(`pickImage` 브릿지). 브라우저에서 열면
 * 고를 수단이 없으므로 `canPick` 이 false 가 되고, 화면은 버튼을 아예 안 그린다 —
 * 눌러도 아무 일이 없는 버튼을 두는 것보다 낫다.
 *
 * `source` 를 넘기지 않는다. 카메라냐 앨범이냐는 OS 가 시트로 묻고, 화면에는 버튼이
 * 하나만 선다. 시트 문구는 여기서 같이 넘긴다 — 네이티브 셸에는 i18n 이 없다.
 *
 * 권한 거부는 **오류가 아니다.** 피커가 이미 시스템 대화상자를 띄웠고 사용자가
 * 거절한 것이므로, 화면에 붉은 오류를 띄우는 대신 설정으로 갈 수 있다는 안내를
 * 돌려준다. 취소는 아무 상태도 남기지 않는다.
 */

/** 아바타 표시 크기의 3배. 네이티브가 이 안으로 줄여서 준다. */
const MAX_SIZE = 256;

/**
 * 피커가 열려 있는 동안은 브릿지 응답이 안 온다 — 사람이 사진을 고를 때까지가
 * 곧 응답 시간이다. 기본 5초(`native-host.ts` `DEFAULT_TIMEOUT`)로는 거의 항상
 * 모자라고, 넘기면 기본 재시도가 `pickImage` 를 **피커가 떠 있는 채로** 다시
 * 보낸다. 네이티브는 그걸 두 번째 피커로 띄우려 하고, iOS 는 거기서 죽는다.
 * 그래서 사람 시간에 맞춘 타임아웃 + 재시도 없음이다.
 */
const PICK_TIMEOUT_MS = 120_000;

export type ProfileImageNotice = 'denied' | 'failed' | null;

export interface UseProfileImage {
  /** data URI 또는 없음. */
  image: string | null;
  /** 사진을 고를 수 있는 환경인가(네이티브 셸). */
  canPick: boolean;
  /** 피커가 떠 있는 동안 true — 버튼 중복 탭을 막는다. */
  picking: boolean;
  /** 마지막 시도에서 사용자에게 알릴 것. 성공·취소면 null. */
  notice: ProfileImageNotice;
  pick: () => Promise<void>;
  remove: () => void;
  dismissNotice: () => void;
}

export const useProfileImage = (): UseProfileImage => {
  const { t } = useTranslation();
  const [image, setImage] = useState<string | null>(() => loadProfileImage());
  const [picking, setPicking] = useState(false);
  const [notice, setNotice] = useState<ProfileImageNotice>(null);

  const pick = useCallback(async () => {
    if (picking) {
      return;
    }
    setPicking(true);
    setNotice(null);
    try {
      const result: PickImageResult = await native.invoke(
        'pickImage',
        {
          maxSize: MAX_SIZE,
          // `source` 를 비워 어디서 가져올지는 OS 가 묻게 한다. 문구는 여기서
          // 넘긴다 — 네이티브 셸에는 i18n 이 없고 로케일은 웹이 안다.
          chooser: {
            title: t('web.profile.photo.label'),
            camera: t('web.profile.photo.camera'),
            library: t('web.profile.photo.library'),
            cancel: t('web.profile.photo.cancel'),
          },
        },
        { timeout: PICK_TIMEOUT_MS, retries: 1 },
      );
      if (result.status === 'picked') {
        // 저장에 실패하면(형식이 아니면) 화면도 바꾸지 않는다 — 새로고침하면
        // 사라질 사진을 보여 주는 것이 더 나쁘다.
        if (saveProfileImage(result.image.dataUri)) {
          setImage(result.image.dataUri);
        } else {
          setNotice('failed');
        }
      } else if (result.status === 'denied') {
        setNotice('denied');
      } else if (result.status === 'failed') {
        setNotice('failed');
      }
      // 'cancelled' 는 아무것도 안 한다.
    } catch {
      // 브릿지가 응답하지 않는 경우(타임아웃 등)도 사용자에겐 실패 하나다.
      setNotice('failed');
    } finally {
      setPicking(false);
    }
  }, [picking, t]);

  const remove = useCallback(() => {
    clearProfileImage();
    setImage(null);
    setNotice(null);
  }, []);

  const dismissNotice = useCallback(() => setNotice(null), []);

  return {
    image,
    canPick: isRunningInNativeApp(),
    picking,
    notice,
    pick,
    remove,
    dismissNotice,
  };
};
